import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { DocumentType } from '@/types/models';

// Default document types to seed for new organizations
const DEFAULT_DOCUMENT_TYPES = [
  { name: 'Contract', description: 'Legal contracts and agreements', color: 'blue', icon: 'file-text', sort_order: 1 },
  { name: 'Warranty', description: 'Warranty documentation', color: 'green', icon: 'shield-check', sort_order: 2 },
  { name: 'Receipt', description: 'Purchase receipts', color: 'yellow', icon: 'receipt', sort_order: 3 },
  { name: 'Invoice', description: 'Invoices and billing documents', color: 'orange', icon: 'file-invoice', sort_order: 4 },
  { name: 'Manual', description: 'User manuals and documentation', color: 'cyan', icon: 'book', sort_order: 5 },
  { name: 'Certificate', description: 'Certificates and licenses', color: 'violet', icon: 'certificate', sort_order: 6 },
  { name: 'Other', description: 'Other documents', color: 'gray', icon: 'file', sort_order: 99 },
];

// Seed default document types for an organization (lazy initialization)
async function seedDefaultDocumentTypes(organizationId: string): Promise<void> {
  const documentsToInsert = DEFAULT_DOCUMENT_TYPES.map((dt) => ({
    organization_id: organizationId,
    name: dt.name,
    description: dt.description,
    color: dt.color,
    icon: dt.icon,
    is_default: true,
    is_active: true,
    sort_order: dt.sort_order,
  }));

  // Use upsert to avoid duplicates
  await supabase
    .from('document_types')
    .upsert(documentsToInsert, { onConflict: 'organization_id,name', ignoreDuplicates: true });
}

// Fetch all document types for an organization (seeds defaults if none exist)
export function useDocumentTypes(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['document-types', organizationId],
    queryFn: async (): Promise<DocumentType[]> => {
      if (!organizationId) return [];

      // First, try to fetch existing document types
      let { data, error } = await supabase
        .from('document_types')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // If no document types exist, seed the defaults
      if (!data || data.length === 0) {
        await seedDefaultDocumentTypes(organizationId);

        // Fetch again after seeding
        const result = await supabase
          .from('document_types')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (result.error) throw result.error;
        data = result.data;
      }

      return data || [];
    },
    enabled: !!organizationId,
  });
}

// Fetch all document types (including inactive) for management
export function useAllDocumentTypes(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['document-types-all', organizationId],
    queryFn: async (): Promise<DocumentType[]> => {
      if (!organizationId) return [];

      // First, try to fetch existing document types
      let { data, error } = await supabase
        .from('document_types')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // If no document types exist, seed the defaults
      if (!data || data.length === 0) {
        await seedDefaultDocumentTypes(organizationId);

        // Fetch again after seeding
        const result = await supabase
          .from('document_types')
          .select('*')
          .eq('organization_id', organizationId)
          .order('sort_order', { ascending: true });

        if (result.error) throw result.error;
        data = result.data;
      }

      return data || [];
    },
    enabled: !!organizationId,
  });
}

// Create a new document type
export function useCreateDocumentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      name,
      description,
      color = 'gray',
      icon = 'file',
    }: {
      organizationId: string;
      name: string;
      description?: string;
      color?: string;
      icon?: string;
    }) => {
      const { data, error } = await supabase
        .from('document_types')
        .insert({
          organization_id: organizationId,
          name,
          description,
          color,
          icon,
          is_default: false,
        })
        .select()
        .single();

      if (error) throw error;
      return data as DocumentType;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['document-types', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['document-types-all', variables.organizationId] });
      notifications.show({
        title: 'Success',
        message: 'Document type created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create document type',
        color: 'red',
      });
    },
  });
}

// Update a document type
export function useUpdateDocumentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      organizationId,
      ...updates
    }: {
      id: string;
      organizationId: string;
      name?: string;
      description?: string;
      color?: string;
      icon?: string;
      is_active?: boolean;
      sort_order?: number;
    }) => {
      const { data, error } = await supabase
        .from('document_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as DocumentType;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['document-types', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['document-types-all', variables.organizationId] });
      notifications.show({
        title: 'Success',
        message: 'Document type updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update document type',
        color: 'red',
      });
    },
  });
}

// Delete a document type (only non-default types)
export function useDeleteDocumentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, organizationId }: { id: string; organizationId: string }) => {
      const { error } = await supabase
        .from('document_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['document-types', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['document-types-all', variables.organizationId] });
      notifications.show({
        title: 'Success',
        message: 'Document type deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete document type',
        color: 'red',
      });
    },
  });
}
